/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Role } from "~/generated/graphql";
import { useCustomers } from "~/services/customers";
import { useBillingPeriod } from "~/services/customers/billingPeriod";
import { isNotEmpty } from "~/tools";
import { ACTION } from "./actions";
import { rules } from "./rules";

enum BILLING_PERIOD {
  current = "current",
  previous = "previous",
}

function validateAccess(
  userRole: Role,
  action: ACTION,
  period: BILLING_PERIOD
) {
  return (
    period != null && (rules[userRole].static as ACTION[]).includes(action)
  );
}

function mergeIntoArray<T>(input: T | T[]): T[] {
  return [...(Array.isArray(input) ? input : [input])];
}

type ActionInput = ACTION | ACTION[];

export function useAccess(input: ActionInput | null = null) {
  const { billingPeriod } = useBillingPeriod();
  const { userRole } = useCustomers();

  const checkAccess = (action: ActionInput): boolean => {
    const mergedArray = mergeIntoArray(action);

    return (
      isNotEmpty(mergedArray) &&
      mergeIntoArray(action).every((act) => {
        return validateAccess(userRole, act, billingPeriod as BILLING_PERIOD);
      })
    );
  };

  return {
    checkAccess,
    hasAccess: input === null ? false : checkAccess(input),
  };
}

type Props = {
  action: ACTION;
  fallback?: React.ReactNode;
  children: React.ReactNode;
};

export function Can({ action, fallback = null, children }: Props) {
  const { hasAccess } = useAccess(action);

  if (hasAccess) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}
